import {
  DrugTestingScheduleInvalidResultType,
  DrugTestingScheduleResult,
  DrugTestingScheduleStatus,
  IDrugTestingLabTest,
  IFacilityAddress,
} from 'models/drugTesting';

import { IDrugTestAppointment } from './types';

// Matches the existing card fallback convention (Project/Type/etc. already use '--').
export const EMPTY_VALUE = '--';

/**
 * Derivation helpers for the extended Appointment view — mirror the Worker App
 * "View mode" so the Worker Portal shows the same fields the same way.
 * Pure where possible; label lookups return translation keys resolved in the card.
 */

// Address — join line1, city, state, zipCode with commas (Worker App parity).
export const getAppointmentAddress = (address?: IFacilityAddress | null): string => {
  if (!address) return EMPTY_VALUE;
  const parts = [address.line1, address.city, address.state, address.zipCode].filter(Boolean);
  return parts.length ? parts.join(', ') : EMPTY_VALUE;
};

// Substance — flat list of sent-to-lab test-substance names (NOT panelTests).
export const getAppointmentSubstance = (substances?: IDrugTestingLabTest[]): string => {
  const names = (substances || []).map(item => item.name).filter(Boolean);
  return names.length ? names.join(', ') : EMPTY_VALUE;
};

// BAT Result is gated on the RESULT being a BAT pass/fail — not on panel name.
// A BAT panel with a non-BAT result (pending / no-show) must not show a BAT row.
export const isBatResultVisible = (result?: number): boolean =>
  result === DrugTestingScheduleResult.BatPass || result === DrugTestingScheduleResult.BatFail;

// Confirmation result is preferred over the primary BAT test result.
export const getBatResult = (appointment: IDrugTestAppointment): string =>
  appointment.batConfirmationTestResult || appointment.batTestResult || EMPTY_VALUE;

// DrugTestingScheduleResult -> translation key ('' means no displayable label).
const RESULT_LABEL_KEYS: Record<number, string> = {
  [DrugTestingScheduleResult.NotSet]: 'myDrugTesting.resultLabels.notSet',
  [DrugTestingScheduleResult.Negative]: 'myDrugTesting.resultLabels.negative',
  [DrugTestingScheduleResult.NonConclusive]: 'myDrugTesting.resultLabels.nonConclusive',
  [DrugTestingScheduleResult.Invalid]: 'myDrugTesting.resultLabels.invalid',
  [DrugTestingScheduleResult.SentToLab]: 'myDrugTesting.resultLabels.sentToLab',
  [DrugTestingScheduleResult.NoShow]: 'myDrugTesting.resultLabels.noShow',
  [DrugTestingScheduleResult.CancelledByWorker]: 'myDrugTesting.resultLabels.cancelledByWorker',
  [DrugTestingScheduleResult.BatPass]: 'myDrugTesting.resultLabels.batPass',
  [DrugTestingScheduleResult.BatFail]: 'myDrugTesting.resultLabels.batFail',
  [DrugTestingScheduleResult.Positive]: 'myDrugTesting.resultLabels.positive',
  [DrugTestingScheduleResult.LostInTransit]: 'myDrugTesting.resultLabels.lostInTransit',
  [DrugTestingScheduleResult.LeakDuringTransit]: 'myDrugTesting.resultLabels.leakDuringTransit',
  [DrugTestingScheduleResult.FatalFlaw]: 'myDrugTesting.resultLabels.fatalFlaw',
  [DrugTestingScheduleResult.Other]: 'myDrugTesting.resultLabels.other',
};

// Test Result field — returns a translation key, or '' when there is no result yet.
export const getResultLabelKey = (result?: number): string =>
  (result !== undefined && RESULT_LABEL_KEYS[result]) || '';

// Raw status -> translation key (used only for the derived-status fallback).
const STATUS_LABEL_KEYS: Record<number, string> = {
  [DrugTestingScheduleStatus.Scheduled]: 'myDrugTesting.appointmentStatus.scheduled',
  [DrugTestingScheduleStatus.InProgress]: 'myDrugTesting.appointmentStatus.inProgress',
  [DrugTestingScheduleStatus.MissedAppointment]: 'myDrugTesting.appointmentStatus.missedAppointment',
  [DrugTestingScheduleStatus.Tested]: 'myDrugTesting.appointmentStatus.tested',
  [DrugTestingScheduleStatus.NoShow]: 'myDrugTesting.appointmentStatus.noShow',
};

// Status tone -> color class in the card. Follows the portal's own status styling
// (the sibling TestResultCard): Pending amber, Approved green, Denied red; every
// other label (Scheduled / No Show / fallback) renders as plain value text.
export type StatusTone = 'pending' | 'approved' | 'denied' | 'normal';

export interface IAppointmentStatusDisplay {
  labelKey: string;
  tone: StatusTone;
}

/**
 * Derived status label — NOT the raw status field. Order matters and mirrors the
 * Worker App ladder exactly:
 *   1. status Scheduled                         -> "Scheduled"  (plain)
 *   2. result NonConclusive | SentToLab         -> "Pending"    (amber)
 *   3. status NoShow                            -> "No Show"    (plain)
 *   4. result Negative | BatPass                -> "Approved"   (green)
 *   5. result Invalid | BatFail | Positive      -> "Denied"     (red)
 *   6. fallback: raw result label (if a real result), else raw status label; plain
 */
export const getAppointmentStatusDisplay = (appointment: IDrugTestAppointment): IAppointmentStatusDisplay => {
  const { status, drugTestingScheduleResult: result } = appointment;

  if (status === DrugTestingScheduleStatus.Scheduled) {
    return { labelKey: 'myDrugTesting.appointmentStatus.scheduled', tone: 'normal' };
  }
  if (result === DrugTestingScheduleResult.NonConclusive || result === DrugTestingScheduleResult.SentToLab) {
    return { labelKey: 'myDrugTesting.appointmentStatus.pending', tone: 'pending' };
  }
  if (status === DrugTestingScheduleStatus.NoShow) {
    return { labelKey: 'myDrugTesting.appointmentStatus.noShow', tone: 'normal' };
  }
  if (result === DrugTestingScheduleResult.Negative || result === DrugTestingScheduleResult.BatPass) {
    return { labelKey: 'myDrugTesting.appointmentStatus.approved', tone: 'approved' };
  }
  if (
    result === DrugTestingScheduleResult.Invalid ||
    result === DrugTestingScheduleResult.BatFail ||
    result === DrugTestingScheduleResult.Positive
  ) {
    return { labelKey: 'myDrugTesting.appointmentStatus.denied', tone: 'denied' };
  }

  // Fallback — prefer the raw result label, but only when it's a real result
  // (NotSet is not); otherwise the raw status label, defaulting to "Unknown".
  const meaningfulResult = result !== undefined && result !== DrugTestingScheduleResult.NotSet;
  const resultKey = meaningfulResult ? getResultLabelKey(result) : '';
  if (resultKey) return { labelKey: resultKey, tone: 'normal' };
  const statusKey =
    (status !== undefined && STATUS_LABEL_KEYS[status]) || 'myDrugTesting.appointmentStatus.unknown';
  return { labelKey: statusKey, tone: 'normal' };
};

// Invalid-result-type -> translation key (only for the fixed enum values 1..4).
const INVALID_RESULT_TYPE_LABEL_KEYS: Record<number, string> = {
  [DrugTestingScheduleInvalidResultType.InsufficientSampleAmount]:
    'myDrugTesting.invalidResultTypes.insufficientSampleAmount',
  [DrugTestingScheduleInvalidResultType.RefusalToProvideASample]:
    'myDrugTesting.invalidResultTypes.refusalToProvideASample',
  [DrugTestingScheduleInvalidResultType.OutOfTemperatureRange]:
    'myDrugTesting.invalidResultTypes.outOfTemperatureRange',
  [DrugTestingScheduleInvalidResultType.AdulteratedSample]: 'myDrugTesting.invalidResultTypes.adulteratedSample',
};

/**
 * Details field — mirrors getInvalidResultTypeText() + its fallback chain:
 *   1..4                -> fixed enum label only (no free text appended)
 *   NotSet (0) / Other  -> invalidResultTypeFreeText, then resultTypeFreeText
 * resultTypeFreeText is a last resort for BOTH NotSet and Other, not the primary
 * source for NotSet. Returns a translation key OR resolved free text.
 */
export const getAppointmentDetails = (appointment: IDrugTestAppointment): { labelKey?: string; text: string } => {
  const type = appointment.drugTestingScheduleInvalidResultType;

  // Fixed labels for the concrete invalid-result types (1..4).
  const labelKey = (type !== undefined && INVALID_RESULT_TYPE_LABEL_KEYS[type]) || '';
  if (labelKey) return { labelKey, text: '' };

  // NotSet (0), Other (5), or unset -> free-text chain.
  return { text: appointment.invalidResultTypeFreeText || appointment.resultTypeFreeText || EMPTY_VALUE };
};
